// frontend/src/services/searchService.js
// 搜尋相關的 API 呼叫服務

import api from './api';

/**
 * 基本搜尋
 * @param {string} query - 搜尋關鍵字
 * @param {Object} options - 選項
 * @param {number} options.page - 頁碼（預設 1）
 * @param {number} options.pageSize - 每頁數量（預設 10）
 * @param {string} options.sortBy - 排序方式（relevance 或 date）
 * @returns {Promise<Object>} 搜尋結果
 */
export const search = async (query, options = {}) => {
  const { page = 1, pageSize = 10, sortBy = 'relevance' } = options;

  try {
    const response = await api.post('/search', {
      query,
      page,
      pageSize,
      sortBy,
    });

    return response.data;
  } catch (error) {
    console.error('基本搜尋失敗:', error);
    throw new Error(error.response?.data?.message || '搜尋失敗，請稍後再試');
  }
};

/**
 * 進階搜尋（支援 AND/OR 邏輯、標籤篩選和來源篩選）
 * @param {string} query - 搜尋關鍵字（支援 + 和 / 符號）
 * @param {Object} options - 選項
 * @param {Array<number>} options.tags - 標籤 ID 陣列
 * @param {Array<string>} options.sources - 來源代碼陣列
 * @param {number} options.page - 頁碼（預設 1）
 * @param {number} options.pageSize - 每頁數量（預設 10）
 * @param {string} options.sortBy - 排序方式（relevance 或 date）
 * @param {boolean} options.enableCrossLanguage - 啟用繁簡互通（預設 true）
 * @param {Array<{keyword:string, operator:string, field:string}>} [options.conditions]
 *   Phase 1B 結構化條件陣列。提供時 backend 走 conditions path（per-condition
 *   field selector），優先於 query 字串解析。空陣列或 undefined 走既有 query path
 *   （向下相容）。
 * @returns {Promise<Object>} 搜尋結果
 */
export const advancedSearch = async (query, options = {}) => {
  const {
    tags = [],
    sources = [],
    page = 1,
    pageSize = 10,
    sortBy = 'relevance',
    enableCrossLanguage = true,
    conditions,
  } = options;

  const payload = {
    query,
    tags,
    sources,
    page,
    pageSize,
    sortBy,
    enableCrossLanguage,
  };

  // Phase 1B: 僅在有有效 conditions 時送出，避免 backend 誤走 conditions path
  if (Array.isArray(conditions) && conditions.length > 0) {
    payload.conditions = conditions;
  }

  try {
    const response = await api.post('/search/advanced', payload);

    return response.data;
  } catch (error) {
    console.error('進階搜尋失敗:', error);
    // ref PC-11 (2026-05-08, Codex Round 1 C4 grounded): preserve warnings + status
    //   修前 throw new Error(message) 重包成 plain Error,axios response 整個丟失,
    //   即便 backend attach response.data.warnings,SearchPage catch 也讀不到
    //   修法: attach warnings 屬性 + status 給 caller (SearchPage) 從 err.warnings 讀取
    const newError = new Error(error.response?.data?.message || '搜尋失敗，請稍後再試');
    newError.warnings = error.response?.data?.warnings || [];
    newError.status = error.response?.status;
    throw newError;
  }
};

/**
 * 取得搜尋建議
 * @param {string} prefix - 搜尋字首
 * @returns {Promise<Array<string>>} 建議的搜尋關鍵字陣列
 */
export const getSuggestions = async (prefix) => {
  if (!prefix || prefix.trim().length === 0) {
    return [];
  }

  try {
    const response = await api.get('/search/suggestions', {
      params: { q: prefix },
    });

    return response.data?.data?.suggestions || [];
  } catch (error) {
    console.error('取得搜尋建議失敗:', error);
    // 建議失敗不影響主要功能，回傳空陣列
    return [];
  }
};

/**
 * 取得標籤列表（用於篩選器）
 * @returns {Promise<Array<Object>>} 標籤陣列
 */
export const getTags = async () => {
  try {
    const response = await api.get('/tags');
    return response.data;
  } catch (error) {
    console.error('取得標籤列表失敗:', error);
    throw new Error('無法載入標籤列表');
  }
};

/**
 * 取得搜尋歷史
 * @param {number} limit - 返回筆數（預設 10）
 * @returns {Promise<Object>} { history: Array, isAuthenticated: boolean }
 */
export const getSearchHistory = async (limit = 10) => {
  try {
    const response = await api.get('/search/history', {
      params: { limit },
    });
    return response.data.data || { history: [], isAuthenticated: false };
  } catch (error) {
    console.error('取得搜尋歷史失敗:', error);
    // 歷史失敗不影響主要功能
    return { history: [], isAuthenticated: false };
  }
};

/**
 * 清除搜尋歷史（需要登入）
 * @returns {Promise<boolean>} 是否成功
 */
export const clearSearchHistory = async () => {
  try {
    await api.delete('/search/history');
    return true;
  } catch (error) {
    console.error('清除搜尋歷史失敗:', error);
    throw new Error(error.response?.data?.message || '清除搜尋歷史失敗');
  }
};
